import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import type { PracticeRecommendation } from '@coffeeplexity/shared/src/types/aiTutoring';

interface PracticeRecommendationsListProps {
  recommendations: PracticeRecommendation[];
  onStartPractice: (recommendation: PracticeRecommendation) => void;
  loading: boolean;
}

const PracticeRecommendationsList: React.FC<
  PracticeRecommendationsListProps
> = ({ recommendations, onStartPractice, loading }) => {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const practiceTypes = [
    { key: 'drill', label: 'Drill', icon: 'repeat', color: '#4CAF50' },
    {
      key: 'scenario',
      label: 'Scenario',
      icon: 'psychology',
      color: '#FF9800',
    },
    { key: 'project', label: 'Project', icon: 'build', color: '#2196F3' },
    { key: 'quiz', label: 'Quiz', icon: 'quiz', color: '#9C27B0' },
    {
      key: 'simulation',
      label: 'Simulation',
      icon: 'science',
      color: '#F44336',
    },
  ];

  const priorities = [
    { key: 'critical', label: 'Critical', color: '#F44336' },
    { key: 'high', label: 'High', color: '#FF9800' },
    { key: 'medium', label: 'Medium', color: '#2196F3' },
    { key: 'low', label: 'Low', color: '#4CAF50' },
  ];

  const filteredRecommendations = recommendations.filter(rec => {
    if (selectedFilters.length === 0) return true;
    return (
      selectedFilters.includes(rec.practiceType) ||
      selectedFilters.includes(rec.skillTargets[0]?.priority || '')
    );
  });

  const toggleFilter = (filter: string) => {
    setSelectedFilters(prev =>
      prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]
    );
  };

  const getPracticeTypeInfo = (
    type: PracticeRecommendation['practiceType']
  ) => {
    return practiceTypes.find(pt => pt.key === type) || practiceTypes[0];
  };

  const getPriorityColor = (priority: string) => {
    return priorities.find(p => p.key === priority)?.color || '#666';
  };

  const handleStartPractice = (recommendation: PracticeRecommendation) => {
    Alert.alert(
      'Start Practice Session',
      `Are you ready to start "${recommendation.skillTargets[0]?.skillName}" practice?\n\nEstimated time: ${recommendation.estimatedDuration} minutes`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Start',
          onPress: () => onStartPractice(recommendation),
          style: 'default',
        },
      ]
    );
  };

  const renderSkillTarget = (
    skillTarget: PracticeRecommendation['skillTargets'][0],
    index: number
  ) => (
    <View key={skillTarget.skillId} style={styles.skillTarget}>
      <View style={styles.skillHeader}>
        <Text style={styles.skillName}>{skillTarget.skillName}</Text>
        <View
          style={[
            styles.priorityBadge,
            { backgroundColor: `${getPriorityColor(skillTarget.priority)}15` },
          ]}
        >
          <Text
            style={[
              styles.priorityText,
              { color: getPriorityColor(skillTarget.priority) },
            ]}
          >
            {skillTarget.priority}
          </Text>
        </View>
      </View>

      <View style={styles.skillProgress}>
        <Text style={styles.skillProgressText}>
          Current: {skillTarget.currentLevel}% → Target:{' '}
          {skillTarget.targetLevel}%
        </Text>
        <View style={styles.progressBar}>
          <View
            style={[
              styles.progressFill,
              { width: `${skillTarget.currentLevel}%` },
            ]}
          />
          <View
            style={[
              styles.progressTarget,
              { left: `${skillTarget.targetLevel}%` },
            ]}
          />
        </View>
      </View>

      {skillTarget.weaknessAreas.length > 0 && (
        <View style={styles.weaknessAreas}>
          <Text style={styles.weaknessTitle}>Focus Areas:</Text>
          <View style={styles.weaknessTags}>
            {skillTarget.weaknessAreas.slice(0, 3).map((area, idx) => (
              <View key={idx} style={styles.weaknessTag}>
                <Text style={styles.weaknessTagText}>{area}</Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );

  const renderRecommendationCard = (recommendation: PracticeRecommendation) => {
    const isExpanded = expandedCard === recommendation.recommendationId;
    const practiceTypeInfo = getPracticeTypeInfo(recommendation.practiceType);
    const primarySkill = recommendation.skillTargets[0];

    return (
      <View
        key={recommendation.recommendationId}
        style={styles.recommendationCard}
      >
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() =>
            setExpandedCard(isExpanded ? null : recommendation.recommendationId)
          }
          activeOpacity={0.7}
        >
          <View style={styles.cardHeaderLeft}>
            <View
              style={[
                styles.practiceTypeIcon,
                { backgroundColor: `${practiceTypeInfo.color}15` },
              ]}
            >
              <Icon
                name={practiceTypeInfo.icon}
                size={20}
                color={practiceTypeInfo.color}
              />
            </View>
            <View style={styles.cardTitleContainer}>
              <Text style={styles.cardTitle}>
                {primarySkill?.skillName || 'Practice Session'}
              </Text>
              <Text style={styles.cardSubtitle}>
                {practiceTypeInfo.label} • {recommendation.estimatedDuration}{' '}
                min
              </Text>
            </View>
          </View>

          <View style={styles.cardHeaderRight}>
            <View
              style={[
                styles.priorityIndicator,
                {
                  backgroundColor: getPriorityColor(
                    primarySkill?.priority || 'medium'
                  ),
                },
              ]}
            />
            <Icon
              name={isExpanded ? 'expand-less' : 'expand-more'}
              size={24}
              color='#666'
            />
          </View>
        </TouchableOpacity>

        {isExpanded && (
          <View style={styles.cardContent}>
            {/* Learning Objectives */}
            <View style={styles.objectivesSection}>
              <Text style={styles.sectionTitle}>Learning Objectives</Text>
              {recommendation.learningObjectives.map((objective, index) => (
                <View key={index} style={styles.objectiveItem}>
                  <Icon name='check-circle-outline' size={16} color='#4CAF50' />
                  <Text style={styles.objectiveText}>{objective}</Text>
                </View>
              ))}
            </View>

            {/* Skill Targets */}
            <View style={styles.skillTargetsSection}>
              <Text style={styles.sectionTitle}>Skill Targets</Text>
              {recommendation.skillTargets.map((skillTarget, index) =>
                renderSkillTarget(skillTarget, index)
              )}
            </View>

            {/* Prerequisites */}
            {recommendation.prerequisites.length > 0 && (
              <View style={styles.prerequisitesSection}>
                <Text style={styles.sectionTitle}>Prerequisites</Text>
                <View style={styles.prerequisitesTags}>
                  {recommendation.prerequisites.map((prereq, index) => (
                    <View key={index} style={styles.prerequisiteTag}>
                      <Text style={styles.prerequisiteText}>{prereq}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Success Criteria */}
            <View style={styles.successSection}>
              <Text style={styles.sectionTitle}>Success Criteria</Text>
              <View style={styles.successCriteria}>
                <View style={styles.criteriaItem}>
                  <Icon name='speed' size={16} color='#2196F3' />
                  <Text style={styles.criteriaText}>
                    Accuracy: {recommendation.successCriteria.accuracyThreshold}
                    %
                  </Text>
                </View>
                {recommendation.successCriteria.speedThreshold && (
                  <View style={styles.criteriaItem}>
                    <Icon name='timer' size={16} color='#FF9800' />
                    <Text style={styles.criteriaText}>
                      Speed: {recommendation.successCriteria.speedThreshold}s
                    </Text>
                  </View>
                )}
                <View style={styles.criteriaItem}>
                  <Icon name='repeat' size={16} color='#4CAF50' />
                  <Text style={styles.criteriaText}>
                    Consistency:{' '}
                    {recommendation.successCriteria.consistencyRequirement}{' '}
                    times
                  </Text>
                </View>
              </View>
            </View>

            {/* Action Button */}
            <TouchableOpacity
              style={[
                styles.startButton,
                { backgroundColor: practiceTypeInfo.color },
              ]}
              onPress={() => handleStartPractice(recommendation)}
              activeOpacity={0.8}
            >
              <Icon name='play-arrow' size={20} color='#fff' />
              <Text style={styles.startButtonText}>Start Practice</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };

  if (loading && recommendations.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <Icon name='fitness-center' size={48} color='#ccc' />
        <Text style={styles.loadingText}>
          Loading practice recommendations...
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Practice Recommendations</Text>
        <Text style={styles.subtitle}>
          AI-curated exercises to improve your skills
        </Text>
      </View>

      {/* Filters */}
      <View style={styles.filtersSection}>
        <Text style={styles.filtersTitle}>Filter by:</Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filtersContainer}
        >
          {practiceTypes.map(type => (
            <TouchableOpacity
              key={type.key}
              style={[
                styles.filterChip,
                selectedFilters.includes(type.key) && {
                  backgroundColor: `${type.color}15`,
                },
              ]}
              onPress={() => toggleFilter(type.key)}
            >
              <Icon
                name={type.icon}
                size={16}
                color={selectedFilters.includes(type.key) ? type.color : '#666'}
              />
              <Text
                style={[
                  styles.filterText,
                  selectedFilters.includes(type.key) && { color: type.color },
                ]}
              >
                {type.label}
              </Text>
            </TouchableOpacity>
          ))}

          {priorities.map(priority => (
            <TouchableOpacity
              key={priority.key}
              style={[
                styles.filterChip,
                selectedFilters.includes(priority.key) && {
                  backgroundColor: `${priority.color}15`,
                },
              ]}
              onPress={() => toggleFilter(priority.key)}
            >
              <Text
                style={[
                  styles.filterText,
                  selectedFilters.includes(priority.key) && {
                    color: priority.color,
                  },
                ]}
              >
                {priority.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Recommendations List */}
      <ScrollView
        style={styles.recommendationsList}
        contentContainerStyle={styles.recommendationsContent}
        showsVerticalScrollIndicator={false}
      >
        {filteredRecommendations.length > 0 ? (
          filteredRecommendations.map(renderRecommendationCard)
        ) : (
          <View style={styles.emptyContainer}>
            <Icon name='fitness-center' size={48} color='#ccc' />
            <Text style={styles.emptyText}>
              {selectedFilters.length > 0
                ? 'No recommendations match your filters'
                : 'No practice recommendations available'}
            </Text>
            {selectedFilters.length > 0 && (
              <TouchableOpacity
                style={styles.clearFiltersButton}
                onPress={() => setSelectedFilters([])}
              >
                <Text style={styles.clearFiltersText}>Clear Filters</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
  },
  filtersSection: {
    marginBottom: 20,
  },
  filtersTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 8,
  },
  filtersContainer: {
    paddingRight: 20,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#f8f9fa',
    marginRight: 8,
    gap: 4,
  },
  filterText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
  },
  recommendationsList: {
    maxHeight: 400,
  },
  recommendationsContent: {
    gap: 16,
  },
  recommendationCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  cardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  practiceTypeIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  cardTitleContainer: {
    flex: 1,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 2,
  },
  cardSubtitle: {
    fontSize: 12,
    color: '#666',
  },
  cardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  priorityIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  cardContent: {
    padding: 16,
    paddingTop: 0,
    gap: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 8,
  },
  objectivesSection: {},
  objectiveItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  objectiveText: {
    fontSize: 14,
    color: '#1a1a1a',
    marginLeft: 8,
    flex: 1,
  },
  skillTargetsSection: {},
  skillTarget: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  skillHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  skillName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    flex: 1,
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  skillProgress: {
    marginBottom: 8,
  },
  skillProgressText: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  progressBar: {
    height: 4,
    backgroundColor: '#e1e5e9',
    borderRadius: 2,
    position: 'relative',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#4CAF50',
    borderRadius: 2,
  },
  progressTarget: {
    position: 'absolute',
    top: -2,
    width: 2,
    height: 8,
    backgroundColor: '#FF9800',
    borderRadius: 1,
  },
  weaknessAreas: {},
  weaknessTitle: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
    marginBottom: 4,
  },
  weaknessTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  weaknessTag: {
    backgroundColor: '#fff3e0',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  weaknessTagText: {
    fontSize: 10,
    color: '#FF9800',
    fontWeight: '500',
  },
  prerequisitesSection: {},
  prerequisitesTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  prerequisiteTag: {
    backgroundColor: '#e3f2fd',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  prerequisiteText: {
    fontSize: 12,
    color: '#2196F3',
    fontWeight: '500',
  },
  successSection: {},
  successCriteria: {
    gap: 6,
  },
  criteriaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  criteriaText: {
    fontSize: 12,
    color: '#666',
  },
  startButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  startButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
    marginTop: 12,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    marginTop: 12,
    textAlign: 'center',
  },
  clearFiltersButton: {
    marginTop: 16,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#007AFF',
  },
  clearFiltersText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#007AFF',
  },
});

export default PracticeRecommendationsList;
